import { RichTableBlockSchema } from '@blocksuite/affine-model';
import {
  BlockMarkdownAdapterExtension,
  type BlockMarkdownAdapterMatcher,
} from '@blocksuite/affine-shared/adapters';
import type { BlockSnapshot, DeltaInsert } from '@blocksuite/store';
import type { PhrasingContent, TableRow } from 'mdast';

/**
 * A GFM table cell is inline-only, so a cell holding several blocks is joined
 * with `<br>` (which GFM renders inside a cell, unlike a real line break) and
 * list items keep a `- ` / `1. ` marker. Nothing is dropped, but the block
 * structure inside a cell flattens.
 *
 * Export to HTML keeps the full structure — see `./html.ts`.
 */
function blockPrefix(node: BlockSnapshot): string {
  if (node.flavour !== 'affine:list') return '';
  const type = node.props.type as string | undefined;
  if (type === 'numbered') return '1. ';
  if (type === 'todo') return node.props.checked ? '[x] ' : '[ ] ';
  return '- ';
}

const BR: PhrasingContent = { type: 'html', value: '<br>' };

export const richTableBlockMarkdownAdapterMatcher: BlockMarkdownAdapterMatcher =
  {
    flavour: RichTableBlockSchema.model.flavour,
    // Import is deliberately not claimed: `affine:table` and `affine:database`
    // already match GFM `table` nodes, and two matchers fighting over one node
    // is worse than not importing.
    toMatch: () => false,
    fromMatch: o => o.node.flavour === RichTableBlockSchema.model.flavour,
    toBlockSnapshot: {},
    fromBlockSnapshot: {
      enter: (o, context) => {
        const { walkerContext, deltaConverter } = context;

        const cellContent = (cell: BlockSnapshot): PhrasingContent[] => {
          const parts: PhrasingContent[] = [];
          for (const child of cell.children ?? []) {
            const delta = ((
              child.props.text as { delta?: DeltaInsert[] } | undefined
            )?.delta ?? []) as DeltaInsert[];
            const ast = deltaConverter.deltaToAST(delta) as PhrasingContent[];
            // An empty paragraph contributes nothing, not a blank line.
            if (ast.length === 0 && !blockPrefix(child)) continue;
            if (parts.length > 0) parts.push(BR);
            const prefix = blockPrefix(child);
            if (prefix) parts.push({ type: 'text', value: prefix });
            parts.push(...ast);
          }
          return parts;
        };

        const rows: TableRow[] = (o.node.children ?? []).map(row => ({
          type: 'tableRow',
          children: (row.children ?? []).map(cell => ({
            type: 'tableCell' as const,
            children: cellContent(cell),
          })),
        }));

        if (rows.length > 0) {
          walkerContext.openNode({ type: 'table', children: rows }).closeNode();
        }

        walkerContext.skipAllChildren();
      },
    },
  };

export const RichTableBlockMarkdownAdapterExtension =
  BlockMarkdownAdapterExtension(richTableBlockMarkdownAdapterMatcher);
